import { useEffect, useState } from "react";
import { GoogleLogo } from "@phosphor-icons/react";
import { useAuth, detectPlatform } from "../lib/auth";

/**
 * AuthLock — translucent scrim that sits ONLY over the prompt box.
 * The home page (logo, headline, subheadline) remains fully visible above it.
 */
export const AuthLock = () => {
  const { login, continueAsGuest } = useAuth();
  const [guestBusy, setGuestBusy] = useState(false);
  const [platform, setPlatform] = useState({ isMobile: false, isIOS: false });
  const [privateMode, setPrivateMode] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  // Best-effort iOS Private Browsing detection (Safari denies localStorage
  // writes in Private mode on older iOS; newer iOS allows it but quota is tiny).
  useEffect(() => {
    try {
      const key = "__recce_pm__";
      localStorage.setItem(key, "1");
      localStorage.removeItem(key);
    } catch {
      setPrivateMode(true);
    }
  }, []);

  const handleGuest = async () => {
    setGuestBusy(true);
    try {
      await continueAsGuest();
    } catch (e) {
      setGuestBusy(false);
    }
  };

  const showIosHint = platform.isIOS;

  return (
    <div
      className="absolute inset-0 z-20 rounded-2xl flex flex-col items-center justify-center px-6 py-5"
      style={{
        background: "#faf7f1",
      }}
      data-testid="auth-lock"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={login}
        data-testid="auth-google-btn"
        className="w-full max-w-[280px] inline-flex items-center justify-center gap-2.5 bg-amber hover:bg-amber-hover text-[#20170a] rounded-full px-5 py-3 text-[14px] font-semibold transition-colors shadow-[0_1px_2px_rgba(28,26,22,0.08)]"
      >
        <GoogleLogo size={16} weight="bold" />
        Continue with Google
      </button>

      <button
        type="button"
        onClick={handleGuest}
        disabled={guestBusy}
        data-testid="auth-guest-btn"
        className="mt-2.5 text-[13px] text-ink-dim hover:text-ink underline underline-offset-4 decoration-ink-ghost hover:decoration-amber transition-colors disabled:opacity-50"
      >
        {guestBusy ? "Entering as guest…" : "Continue as a guest"}
      </button>

      {showIosHint && (
        <p
          data-testid="auth-ios-hint"
          className="mt-3 text-center text-[10.5px] text-ink-ghost leading-snug max-w-[280px]"
        >
          {privateMode
            ? "On iPhone Private Browsing, Google sign-in is unreliable — tap Continue as a guest."
            : "On iPhone, if Google sign-in gets stuck, come back here and tap Continue as a guest."}
        </p>
      )}

      <p
        data-testid="auth-terms"
        className="mt-3 text-center text-[9.5px] text-ink-ghost leading-snug max-w-[300px]"
      >
        By continuing you accept Recce's{" "}
        <a href="#" className="underline underline-offset-2 hover:text-amber">terms</a>
        {" "}and{" "}
        <a href="#" className="underline underline-offset-2 hover:text-amber">privacy notice</a>.
      </p>
    </div>
  );
};
